import { Component, Inject, OnInit, ViewChild } from "@angular/core";
import { FormBuilder, FormGroup } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from "@angular/material/dialog";
import { MatSelect } from "@angular/material/select";
import { AuthorizedUser, ConfigStorage, getFormErrorList, isObjectValidator, requiredIfValidator, requiredValidator, UserStorage } from "@scisys-uim/uim-common";
import { AssetsUser, BUSINESS_UNIT_METADATA, BUSINESS_UNIT_TYPE, BusinessUnit, COST_CENTRE_METADATA, CostCentre, Firearm, FIREARM_ASSIGNABLE_PERSON_METADATA, FIREARM_ASSIGNABLE_PERSON_TYPE, FIREARM_STORAGE_LOCATION_METADATA, FIREARM_STORAGE_LOCATION_TYPE, FIREARM_TYPE, FirearmAssignablePerson, FirearmAssignment, FirearmAssignmentLog, FirearmPool, FirearmStorageLocation } from "assets-model";
import { BusinessUnitService, CostCentreService, FirearmAssignablePersonService, FirearmAssignmentLogService, FirearmPoolService, FirearmService, FirearmStorageLocationService } from "assets-service";
import { BehaviorSubject, combineLatest, Observable, of, Subject, Subscription } from "rxjs";
import { filter, map, tap } from "rxjs/operators";
import { AutocompleteStringFilterFunctionAsync } from "../../../shared/form/autocomplete.util";



export interface FirearmTransferDialogData {
  businessUnit?: BusinessUnit;
  costCentre?: CostCentre;
  storageAssignee?: FirearmAssignablePerson;
  location?: FirearmStorageLocation;
  comments?: string;
  firearm?: Firearm;
}

export interface FirearmTransferDialogConfigData {
  firearm?: Firearm;
  storageAssignee?: FirearmAssignablePerson;
  storageAssigneeSelectionListFilterLoad?: (text: string) => Observable<FirearmAssignablePerson[]>;
}

@Component({
  selector: 'app-firearm-transfer-dialog',
  templateUrl: './firearm-transfer-dialog.component.html',
  styleUrls: ['./firearm-transfer-dialog.component.scss']
})
export class FirearmTransferDialogComponent implements OnInit {

  protected systemConfig: any;

  public form: FormGroup;

  public item: FirearmTransferDialogData = {
  };

  protected userDetails: AuthorizedUser;
  firearmAssignmentLog: FirearmAssignmentLog;
  currentAssignment: FirearmAssignment;
  firearmPool: FirearmPool;

  get eAssetsUser(): AssetsUser {
    return AssetsUser.assignAs(this.userDetails);
  }

  get hasPendingActions(): boolean {
    return this.firearmAssignmentLog?.pendingActions;
  }

  get currentlyIssued(): boolean {
    return this.data?.firearm?.currentAssignmentFirearmAssignmentType == 'ISSUE';
  }

  get firearmTypeName(): string {
    return FIREARM_TYPE.typeName(this.data?.firearm);
  }

  get firearmDescription(): string {
    return FIREARM_TYPE.description(this.data?.firearm);
  }

  get poolManagerUsername(): string {
    return this.firearmPool?.manager?.user;
  }

  get storageAssigneeUsername(): string {
    return this.item?.storageAssignee?.user?.user;
  }

  get assigneeConflictsWithPoolManager(): boolean {
    if (this.firearmPool == null) {
      return false;
    }
    return this.storageAssigneeUsername != null && this.storageAssigneeUsername != this.poolManagerUsername;
  }

  get transferWithinBusinessUnit(): boolean {
    return this.data?.firearm?.asset?.businessUnit?.buId == this.item?.businessUnit?.buId;
  }

  businessUnitSelectionList: BusinessUnit[];
  costCentreSelectionList: CostCentre[];

  private _onInitCompleteSubject: Subject<boolean> = new BehaviorSubject<boolean>(null);
  $onInitComplete: Observable<boolean> = this._onInitCompleteSubject.asObservable().pipe(filter(o => o != null));
  private onInitSubscription: Subscription = null;

  @ViewChild('storageAssignee') storageAssigneeInput: MatSelect;
  storageAssigneeSelectionList: FirearmAssignablePerson[] = [];

  @ViewChild('location') locationInput: MatSelect;
  locationSelectionList: FirearmStorageLocation[] = [];

  constructor(
    private dialogRef: MatDialogRef<FirearmTransferDialogComponent, FirearmTransferDialogData>,
    @Inject(MAT_DIALOG_DATA) public data: FirearmTransferDialogConfigData,
    private fb: FormBuilder,
    private dialog: MatDialog,
    private firearmService: FirearmService,
    private firearmAssignmentLogService: FirearmAssignmentLogService,
    private firearmAssignablePersonService: FirearmAssignablePersonService,
    protected firearmStorageLocationService: FirearmStorageLocationService,
    protected firearmPoolService: FirearmPoolService,
    private businessUnitService: BusinessUnitService,
    private costCentreService: CostCentreService,
    protected configService: ConfigStorage,
    protected userStorage: UserStorage) {

    dialogRef.disableClose = true;

    this.data = this.data || {
    };

    this.form = this.fb.group({
      businessUnit: [null, [requiredValidator]],
      costCentre: [],
      storageAssignee: [null, [requiredValidator]],
      location: [null, [requiredValidator]],
      comments: [null, [requiredValidator]]
    },
      {
        validators: [
          (control) => {
            return this.isChangeToCurrentAssignment(true, true) ? null : { notMovingLocation: "The firearm is already assigned to this Storage Location and Assignee." };
          }
        ]
      });

    this.firearmAssignmentLogService.get(data.firearm?.asset?.assetId).subscribe(result => {
      this.firearmAssignmentLog = result;
      this.currentAssignment = this.firearmAssignmentLog.getCurrentAssignment();
    });

    if (data.firearm?.poolFirearm) {
      this.firearmPoolService.get(data.firearm?.asset?.assetId).subscribe(result => {
        this.firearmPool = result;
      });
    }

    this.setDefaultDisabledFields();
  }

  protected onInitAsyncComplete() {
    this.storageAssigneeLoadSelectionList();
    this.locationLoadSelectionList();
    this.businessUnitLoadSelectionList();
  }

  ngAfterViewInit() {
  }

  setDefaultDisabledFields() {
    if (!this.form) {
      return;
    }
    if (this.data.storageAssignee != null) {
      this.storageAssignee.disable();
    }
  }

  closeDialog() {
    this.dialogRef.close();
  }

  isChangeToCurrentAssignment(usePreciseLocation: boolean, useFormValues: boolean = false) {
    // Ownership can change while a firearm remains at the same national store.
    const businessUnit = useFormValues ? this.form?.get('businessUnit')?.value : this.item?.businessUnit;
    const costCentre = useFormValues ? this.form?.get('costCentre')?.value : this.item?.costCentre;
    if (this.data.firearm?.asset?.businessUnit?.buId != businessUnit?.buId
        || this.data.firearm?.asset?.costCentre?.ccId != costCentre?.ccId) {
      return true;
    }
    if (this.currentAssignment?.firearmAssignmentType != 'STORAGE') {
      return true;
    }
    var storageAssignee = useFormValues ? this.storageAssignee.value : this.item?.storageAssignee;
    if (this.currentAssignment?.user?.user != storageAssignee?.user?.user) {
      return true;
    }
    var location = useFormValues ? this.location.value : this.item?.location;
    if (usePreciseLocation) {
      if (this.currentAssignment?.location?.firearmStorageLocationId != location?.firearmStorageLocationId) {
        return true;
      }
    } else {
      if (this.currentAssignment?.location?.businessUnit?.buId != location?.businessUnit?.buId) {
        return true;
      }
    }
    return false;
  }

  isFirearmTransferEnabled() {
    return !this.hasPendingActions && !this.currentlyIssued && !this.form.invalid && this.isChangeToCurrentAssignment(true);
  }

  firearmTransfer() {
    this.firearmService.transferFirearm({
      assetId: this.data.firearm?.assetId,
      buId: this.item?.businessUnit?.buId,
      ccId: this.item?.costCentre?.ccId,
      storageAssignee: {
        personId: this.item?.storageAssignee?.personId,
        username: this.item?.storageAssignee?.user?.user
      },
      locationId: this.item?.location?.firearmStorageLocationId,
      comments: this.item.comments
    }).subscribe(result => {
      this.item.firearm = result;
      this.dialogRef.close(this.item);
    });
  }

  getFormErrorList() {
    var result = [...getFormErrorList(this.form)];
    return result;
  }



  businessUnitLoadSelectionList() {
    this.businessUnitService.findAll(false).subscribe(
      data => {
        this.businessUnitSelectionList = data.items;
      }
    );
  }

  businessUnitChanged(value: BusinessUnit, unsetDependents: boolean = true) {
    this.costCentreLoadSelectionList();
    this.storageAssigneeLoadSelectionList();
    this.locationLoadSelectionList();
  }

  businessUnitCompare(a: any, b: any) {
    return BUSINESS_UNIT_METADATA.equals(a, b);
  }

  businessUnitDisplay(element: BusinessUnit) {
    return BUSINESS_UNIT_TYPE.description(element);
  }


  costCentreLoadSelectionList() {
    if (this.item?.businessUnit == null) {
      this.costCentreSelectionList = [];
      this.item.costCentre = null;
      return;
    }
    var filters = { buId: this.item?.businessUnit.buId }
    this.costCentreService.findByFullFilters(filters).subscribe(
      data => {
        this.costCentreSelectionList = data.items;
      }
    );
  }

  costCentreChanged(value: CostCentre, unsetDependents: boolean = true) {
  }


  costCentreCompare(a: any, b: any) {
    return COST_CENTRE_METADATA.equals(a, b);
  }


  // Person field


  storageAssigneeLoadSelectionList() {
    if (this.item?.businessUnit == null) {
      this.storageAssigneeSelectionList = [];
      this.item.storageAssignee = null;
      return;
    }
    this.firearmAssignablePersonService.findByFullFilters({
      buId: this.item?.businessUnit?.buId,
      assignmentType: 'STORAGE'
    }).subscribe(
      data => {
        this.storageAssigneeSelectionList = data.items;
        if (this.item.storageAssignee == null && this.storageAssigneeSelectionList?.length == 1) {
          this.item.storageAssignee = this.storageAssigneeSelectionList[0];
        }
      }
    );
  }

  storageAssigneeDisplay(item: FirearmAssignablePerson) {
    if (item == null) {
      return "";
    }
    if (typeof item == 'string') {
      return item;
    }
    return FIREARM_ASSIGNABLE_PERSON_TYPE.description(item);
  }

  storageAssigneeChanging(value: FirearmAssignablePerson, unsetDependents: boolean = true) { }

  storageAssigneeChanged(value: FirearmAssignablePerson, unsetDependents: boolean = true) {
    this.locationLoadSelectionList();
  }

  storageAssigneeCompare(a: any, b: any) {
    return FIREARM_ASSIGNABLE_PERSON_METADATA.equals(a, b);
  }

  get storageAssignee() {
    return this.control('storageAssignee');
  }

  // Location field

  locationLoadSelectionList() {
    if (this.item?.businessUnit == null) {
      this.locationSelectionList = [];
      this.item.location = null;
      return;
    }
    this.firearmStorageLocationService.findByFullFilters({
      includeNational: true,
      buId: this.item.businessUnit?.buId,
    }).subscribe(
      data => {
        this.locationSelectionList = data.items;
        if (this.item.location == null && this.locationSelectionList?.length == 1) {
          this.item.location = this.locationSelectionList[0];
        }
      }
    );
  }

  locationDisplay(item: FirearmStorageLocation) {
    if (item == null) {
      return "";
    }
    if (typeof item == 'string') {
      return item;
    }
    return FIREARM_STORAGE_LOCATION_TYPE.description(item);
  }

  locationChanging(value: string, unsetDependents: boolean = true) { }

  locationChanged(value: string, unsetDependents: boolean = true) {
  }

  locationCompare(a: any, b: any) {
    return FIREARM_STORAGE_LOCATION_METADATA.equals(a, b);
  }


  get location() {
    return this.control('location');
  }

  // Comments field
  commentsChanging(value: string, unsetDependents: boolean = true) { }

  commentsChanged(value: string, unsetDependents: boolean = true) {
  }

  get comments() {
    return this.control('comments');
  }

  // form controls general

  control(name: string) {
    return this.form ? this.form.get(name) : null;
  }

  // 'framework'

  ngOnInit() {
    this.onInitAsync();
  }


  protected onInitAsync() {
    const allObservables: Observable<any>[] = this.onInitAsyncObservables() || [];
    this.onInitSubscription = combineLatest(allObservables).subscribe(() => {
      this.onInitSubscription?.unsubscribe();
      this._onInitAsyncComplete();
    });
  }

  protected _onInitAsyncComplete() {
    this.onInitAsyncComplete();
    this.configService.itemChanges().subscribe((configData: any) => this.initConfig(configData));
    this._onInitCompleteSubject.next(true);
    this._onInitCompleteSubject.complete();
  }

  // These observables will be waited for, on page load, before the screen is ready to search.
  // These Observables are required by the framework - take care in overriding
  protected onInitAsyncObservables(): Observable<any>[] {
    return [this.onInitConfigObservable(),
    this.onInitUserDetails()];
  }
  protected onInitConfigObservable(): Observable<any> {
    return this.configService.resolve().pipe(tap((configData: any) => this.initConfig(configData)));
  }
  protected initConfig(config: any) {
    this.systemConfig = config || {};
  }
  protected onInitUserDetails(): Observable<AuthorizedUser> {
    return this.userStorage.nextNonNullItem().pipe(tap((userDetails: AuthorizedUser) => this.initUserDetails(userDetails)));
  }
  protected initUserDetails(userDetails: AuthorizedUser) {
    this.userDetails = userDetails;
  }


  disabledTooltip(element) {
    return element && (element.valid || !element.touched || !element.value);
  }
}